"use client";

import Image, { ImageProps } from "next/image";
import { useState } from "react";
import { ImageOff } from "lucide-react";
import { useTranslations } from "next-intl";

interface SafeImageProps extends ImageProps {
  fallbackSrc?: string;
  hideOnError?: boolean;
  onValidationError?: (hasError: boolean) => void;
}

function normalizeSrc(s: ImageProps["src"]): ImageProps["src"] {
  if (typeof s !== "string" || s === "") return s;

  let normalized = s.replace(/\\/g, "/");

  if (normalized.startsWith("http://api.almasriamedwaste.com")) {
    normalized = normalized.replace("http://", "https://");
  }

  if (
    normalized.startsWith("images/") ||
    normalized.startsWith("general/") ||
    normalized.startsWith("storage/")
  ) {
    const path = normalized.startsWith("storage/")
      ? normalized
      : `storage/${normalized}`;
    return `https://api.almasriamedwaste.com/${path}`;
  }

  return normalized;
}

export function SafeImage({
  src,
  alt,
  fallbackSrc,
  hideOnError,
  onValidationError,
  ...props
}: SafeImageProps) {
  const t = useTranslations("Common");
  const defaultFallback =
    "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=800&auto=format&fit=crop";

  const [errorCount, setErrorCount] = useState(0);
  const normalizedSrc = normalizeSrc(src);

  const resolvedSrc =
    typeof normalizedSrc === "string" && normalizedSrc.trim() !== ""
      ? normalizedSrc
      : fallbackSrc || defaultFallback;

  const showFallbackImage = errorCount === 1;
  const showErrorMessage = errorCount >= 2;

  if (showErrorMessage) {
    if (hideOnError) return null;
    return (
      <div
        className={`flex flex-col items-center justify-center bg-slate-100 dark:bg-slate-800 text-slate-400 p-4 rounded-xl ${props.className || ""}`}
      >
        <ImageOff className="w-10 h-10 mb-2 opacity-50" />
        <p className="text-[10px] text-center font-bold">
          {t("image_error")}
        </p>
      </div>
    );
  }

  const finalSrc = showFallbackImage
    ? fallbackSrc || defaultFallback
    : resolvedSrc;

  return (
    <Image
      key={src as string}
      {...props}
      src={finalSrc}
      alt={alt}
      onError={() => {
        setErrorCount((c) => c + 1);
        if (errorCount >= 1) {
          onValidationError?.(true);
        }
      }}
    />
  );
}
